import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import config from "../consts";

interface IncidentType {
  type_id: number;
  name: string;
  description?: string;
}

interface User {
  username_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export default function PanicSettingsPage() {
  const [recipients, setRecipients] = useState<User[]>([]);
  const [incidentTypes, setIncidentTypes] = useState<IncidentType[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [addRecipientOpen, setAddRecipientOpen] = useState(false);
  const [addIncidentOpen, setAddIncidentOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newIncidentName, setNewIncidentName] = useState("");
  const [newIncidentDescription, setNewIncidentDescription] = useState("");

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      let { data: users } = await axios.get(`${config.api}/v2/users`);

      // let { data: incident_types } = await axios.get(
      //   `${config.api}/v2/cig/panic_alert/incident-types`,
      // );
      // console.log("incident_types: ", incident_types);

      setAvailableUsers(users as User[]);
    } catch (err) {
      setError("Failed to load settings data");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = async () => {
    try {
      if (!selectedUser) {
        setError("Please select a user");
        return;
      }

      if (recipients.some((r) => r.username_id === selectedUser.username_id)) {
        setError("User is already a recipient");
        return;
      }

      setRecipients([...recipients, selectedUser]);
      setAddRecipientOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError("Failed to add recipient");
      console.error("Error adding recipient:", err);
    }
  };

  const handleRemoveRecipient = async (userId: number) => {
    try {
      // In a real app, this would be an API call to DELETE /panic-alert-recipients
      console.log("Removing recipient:", userId);

      setRecipients(recipients.filter((r) => r.username_id !== userId));
    } catch (err) {
      setError("Failed to remove recipient");
      console.error("Error removing recipient:", err);
    }
  };

  const handleAddIncidentType = async () => {
    try {
      if (!newIncidentName.trim()) {
        setError("Please enter an incident type name");
        return;
      }

      // In a real app, this would be an API call to POST /incident-types
      console.log("Adding incident type:", newIncidentName);

      const newIncident: IncidentType = {
        type_id: Math.max(...incidentTypes.map((i) => i.type_id)) + 1,
        name: newIncidentName,
        description: newIncidentDescription,
      };

      setIncidentTypes([...incidentTypes, newIncident]);
      setAddIncidentOpen(false);
      setNewIncidentName("");
      setNewIncidentDescription("");
    } catch (err) {
      setError("Failed to add incident type");
      console.error("Error adding incident type:", err);
    }
  };

  const handleRemoveIncidentType = async (typeId: number) => {
    try {
      // this would be an API call to DELETE /incident-types
      console.log("Removing incident type:", typeId);

      setIncidentTypes(incidentTypes.filter((i) => i.type_id !== typeId));
    } catch (err) {
      setError("Failed to remove incident type");
      console.error("Error removing incident type:", err);
    }
  };

  const getAvailableUsersForSelection = () => {
    return availableUsers.filter(
      (user) =>
        !recipients.some(
          (recipient) => recipient.username_id === user.username_id,
        ),
    );
  };

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{ py: 6, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 6, fontWeight: 500 }}>
        Panic Notification Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {/* Panic Alert Recipients Section */}
        <Paper
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
        >
          <Box sx={{ p: 3, pb: 0 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <PersonIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 500 }}
                >
                  Alert Recipients
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddRecipientOpen(true)}
                size="small"
                sx={{ minWidth: "auto" }}
              >
                Add
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select existing users to receive panic notifications
            </Typography>
          </Box>

          <List sx={{ pt: 0 }}>
            {recipients.map((recipient, index) => (
              <Box key={recipient.username_id}>
                <ListItem sx={{ px: 3, py: 2 }}>
                  <ListItemText
                    primary={`${recipient.first_name} ${recipient.last_name}`}
                    secondary={recipient.email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ color: "text.secondary" }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() =>
                        handleRemoveRecipient(recipient.username_id)
                      }
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < recipients.length - 1 && <Divider sx={{ mx: 3 }} />}
              </Box>
            ))}
            {recipients.length === 0 && (
              <ListItem sx={{ px: 3, py: 4 }}>
                <ListItemText
                  primary="No recipients configured"
                  secondary="Add users to receive panic alerts"
                  sx={{ textAlign: "center" }}
                  primaryTypographyProps={{ color: "text.secondary" }}
                  secondaryTypographyProps={{ color: "text.disabled" }}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        {/* Incident Types Section */}
        <Paper
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
        >
          <Box sx={{ p: 3, pb: 0 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <WarningIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 500 }}
                >
                  Incident Types
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddIncidentOpen(true)}
                size="small"
                sx={{ minWidth: "auto" }}
              >
                Add
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Available incident categories
            </Typography>
          </Box>

          <List sx={{ pt: 0 }}>
            {incidentTypes.map((incident, index) => (
              <Box key={incident.type_id}>
                <ListItem sx={{ px: 3, py: 2 }}>
                  <ListItemText
                    primary={incident.name}
                    secondary={incident.description}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ color: "text.secondary" }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveIncidentType(incident.type_id)}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < incidentTypes.length - 1 && <Divider sx={{ mx: 3 }} />}
              </Box>
            ))}
            {incidentTypes.length === 0 && (
              <ListItem sx={{ px: 3, py: 4 }}>
                <ListItemText
                  primary="No incident types configured"
                  secondary="Add categories to classify alerts"
                  sx={{ textAlign: "center" }}
                  primaryTypographyProps={{ color: "text.secondary" }}
                  secondaryTypographyProps={{ color: "text.disabled" }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>

      <Dialog
        open={addRecipientOpen}
        onClose={() => setAddRecipientOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Add Recipient</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Autocomplete
            options={getAvailableUsersForSelection()}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name} (${option.email})`
            }
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                variant="outlined"
                size="small"
                helperText="Choose an existing user to add as recipient"
              />
            )}
            noOptionsText="No available users"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddRecipientOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleAddRecipient} variant="contained" size="small">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addIncidentOpen}
        onClose={() => setAddIncidentOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Add Incident Type</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            variant="outlined"
            value={newIncidentName}
            onChange={(e) => setNewIncidentName(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={newIncidentDescription}
            onChange={(e) => setNewIncidentDescription(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddIncidentOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddIncidentType}
            variant="contained"
            size="small"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
